import React from 'react';
import { Link } from 'react-router-dom';

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  marginBottom: '2rem',
  fontSize: '0.95rem'
};

const thTdStyle: React.CSSProperties = {
  border: '1px solid #ddd',
  padding: '0.75rem 1rem',
  textAlign: 'left',
  verticalAlign: 'top'
};

const CPGPrivacyPolicy: React.FC = () => {
  return (
    <section className="section">
      <div className="container">
        <div style={{ marginBottom: '2rem' }}>
          <Link 
            to="/" 
            style={{ 
              color: 'var(--accent-color)', 
              textDecoration: 'none',
              fontSize: '1rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            ← Back to Home
          </Link>
        </div>
        
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h1 style={{ 
            fontSize: '2.5rem', 
            fontWeight: '700',
            marginBottom: '1rem',
            background: 'linear-gradient(135deg, #7c3aed 0%, #db2777 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            CPG - Chord Progression Generator Privacy Policy
          </h1>
          <div className="section-divider"></div>
        </div>

        <div style={{ 
          maxWidth: '800px', 
          margin: '0 auto',
          lineHeight: '1.6',
          fontSize: '1rem'
        }}>
          <p style={{ marginBottom: '2rem', fontSize: '1rem' }}>
            <strong>Effective Date:</strong> August 26, 2026<br/>
            <strong>App:</strong> CPG (Chord Progression Generator)<br/>
            <strong>Developer / Controller:</strong> the publisher of CPG on the Apple App Store (<code>com.chordprogression.gen</code>)<br/>
            <strong>Contact:</strong> privacy@chordprogressiongen.com
          </p>

          <p style={{ marginBottom: '1rem' }}>
            This Privacy Policy explains how CPG ("we", "us", or "our") collects, uses, and shares information when you use our mobile application.
          </p>
          <p style={{ marginBottom: '2rem' }}>
            If you do not agree with this policy, please do not use the app.
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>1. Summary</h2>
          <p style={{ marginBottom: '1rem' }}>
            CPG helps you generate, practice, save, and export chord progressions. We aim to collect only what is needed to run the app, process purchases, and improve generation quality.
          </p>
          <p style={{ marginBottom: '2rem' }}>
            We do <strong>not</strong> sell your personal information.
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>2. Information We Collect</h2>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>2.1 Information You Provide</h3>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>Generation inputs</strong> you type or select in the app (for example prompts, moods, key, difficulty, instrument, and chord count). These are sent to our servers so we can generate a progression.</li>
            <li><strong>Saved charts and preferences</strong> you create in the app (library items, theme, default instrument, onboarding status, and related settings). Much of this is stored on your device.</li>
            <li><strong>Support messages</strong> you send us if you contact us by email or through the App Store.</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>2.2 Information Collected Automatically</h3>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>Anonymous account identifier.</strong> When cloud features are available, the app may create an anonymous Firebase Auth user ID so generation requests and Premium status can be associated with your install (not with your name or email, unless you later choose to provide them).</li>
            <li><strong>Purchase and subscription data.</strong> If you buy Premium, Apple processes the payment. We use RevenueCat to verify entitlements (active subscription status, product identifiers, and related purchase metadata). We do not receive your full payment card details.</li>
            <li><strong>Device and diagnostics data.</strong> Limited technical information may be processed by Apple, Firebase, or RevenueCat to operate the service (for example app version, device type, OS version, and error logs).</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>2.3 Information We Do Not Intentionally Collect</h3>
          <p style={{ marginBottom: '2rem' }}>
            We do not require you to create a username/password account with your real name. We do not ask for contacts, photos, precise location, or microphone access for the core product experience described above.
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>3. How We Use Information</h2>
          <p style={{ marginBottom: '0.5rem' }}>We use the information above to:</p>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li>Generate chord progressions and related content you request</li>
            <li>Save and restore your library and settings on your device</li>
            <li>Provide, restore, and manage Premium subscriptions</li>
            <li>Enforce free-tier limits (for example weekly generation quotas)</li>
            <li>Operate, secure, and troubleshoot the app and backend</li>
            <li>Comply with law and App Store / platform requirements</li>
          </ul>
          <p style={{ marginBottom: '2rem' }}>
            Generation prompts and related inputs may be processed by third-party AI infrastructure acting on our behalf to produce results. Do not include sensitive personal data in prompts.
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>4. How We Share Information</h2>
          <p style={{ marginBottom: '0.5rem' }}>
            We share information only as needed with service providers who help us run CPG, including:
          </p>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thTdStyle}>Provider</th>
                <th style={thTdStyle}>Purpose</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={thTdStyle}><strong>Apple</strong></td>
                <td style={thTdStyle}>App distribution, In-App Purchases, and related platform services</td>
              </tr>
              <tr>
                <td style={thTdStyle}><strong>RevenueCat</strong></td>
                <td style={thTdStyle}>Subscription / entitlement management</td>
              </tr>
              <tr>
                <td style={thTdStyle}><strong>Google Firebase</strong></td>
                <td style={thTdStyle}>Anonymous authentication and cloud functions used for generation</td>
              </tr>
              <tr>
                <td style={thTdStyle}><strong>AI generation vendors</strong> (via our backend)</td>
                <td style={thTdStyle}>Producing chord progressions from your brief</td>
              </tr>
            </tbody>
          </table>
          <p style={{ marginBottom: '1rem' }}>
            These providers process data under their own terms and privacy policies, and only for the purposes described here (or as required by law).
          </p>
          <p style={{ marginBottom: '1rem' }}>
            We may also disclose information if required by law, to protect rights and safety, or in connection with a merger, acquisition, or sale of assets (in which case we will take reasonable steps to keep protections in place).
          </p>
          <p style={{ marginBottom: '2rem' }}>
            We do <strong>not</strong> sell personal information, and we do not share it for cross-context behavioral advertising.
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>5. Data Stored on Your Device</h2>
          <p style={{ marginBottom: '2rem' }}>
            Charts, preferences, and local settings are stored on your device (for example via on-device storage). If you delete the app or clear app data, this local information may be permanently lost unless you have exported or backed it up yourself.
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>6. Retention</h2>
          <ul style={{ marginBottom: '2rem', paddingLeft: '1.5rem' }}>
            <li><strong>On-device data</strong> remains until you delete it in the app, clear app storage, or uninstall.</li>
            <li><strong>Server-side identifiers and generation requests</strong> are kept only as long as needed to provide the service, prevent abuse, debug issues, and meet legal obligations.</li>
            <li><strong>Purchase / entitlement records</strong> are retained as needed to honor subscriptions and comply with financial and platform rules.</li>
          </ul>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>7. Security</h2>
          <p style={{ marginBottom: '2rem' }}>
            We use reasonable administrative and technical measures appropriate to the nature of the data (including HTTPS for network requests and platform security features). No method of transmission or storage is 100% secure.
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>8. Children's Privacy</h2>
          <p style={{ marginBottom: '2rem' }}>
            CPG is not directed to children under 13 (or the minimum age required in your country). We do not knowingly collect personal information from children. If you believe a child has provided us information, contact us and we will take appropriate steps to delete it.
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>9. Your Choices and Rights</h2>
          <p style={{ marginBottom: '1rem' }}>
            Depending on where you live, you may have rights to access, correct, delete, or restrict certain processing of your personal information, or to object to processing.
          </p>
          <p style={{ marginBottom: '0.5rem' }}>You can:</p>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li>Delete saved charts and clear local data in the app (where available)</li>
            <li>Manage or cancel subscriptions in your Apple ID subscription settings</li>
            <li>Uninstall the app to remove local data from that device</li>
            <li>Contact us to request help with account/identifier deletion related to our backend</li>
          </ul>
          <p style={{ marginBottom: '2rem' }}>
            For Apple subscriptions, billing and cancellation are handled by Apple.
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>10. International Transfers</h2>
          <p style={{ marginBottom: '2rem' }}>
            We and our providers may process information in countries other than yours (including the United States). Where required, we rely on appropriate safeguards for such transfers.
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>11. Third-Party Links and Exports</h2>
          <p style={{ marginBottom: '2rem' }}>
            If you export or share charts (for example ChordPro, PDF, or audio) through the system share sheet, the destination apps are outside our control. Their privacy practices apply once you share.
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>12. Changes to This Policy</h2>
          <p style={{ marginBottom: '2rem' }}>
            We may update this Privacy Policy from time to time. The "Effective date" at the top will change when we do. Continued use of the app after an update means you accept the revised policy.
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>13. Contact</h2>
          <p style={{ marginBottom: '1rem' }}>
            For privacy questions or requests, contact us at:
          </p>
          <p style={{ marginBottom: '1rem' }}>
            <strong>Email:</strong> privacy@chordprogressiongen.com
          </p>
          <p style={{ marginBottom: '2rem' }}>
            (If that address is not yet active, use the support contact listed on our App Store product page.)
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>14. Terms of Use (iOS)</h2>
          <p style={{ marginBottom: '2rem' }}>
            Your use of CPG on iOS is also subject to Apple's standard Licensed Application End User License Agreement:{' '}
            <a 
              href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" 
              target="_blank" 
              rel="noopener noreferrer" 
              style={{ color: 'var(--accent-color)' }}
            >
              https://www.apple.com/legal/internet-services/itunes/dev/stdeula/
            </a>
          </p>

          <hr style={{ margin: '3rem 0', border: 'none', borderTop: '1px solid #eee' }} />
          <p style={{ marginBottom: '0', fontSize: '0.9rem', color: '#666', fontStyle: 'italic' }}>
            This document is the privacy policy for the CPG (Chord Progression Generator) iOS application.
          </p>
        </div>
      </div>
    </section>
  );
};

export default CPGPrivacyPolicy;
